import { Injectable } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { filter, map } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NotificationConfig, NotificationConfigBuilder } from './notification-config';
import { RoutingNames } from '../router/src';

@Injectable({
  providedIn: 'root',
})
export class NotificationService {
  private static max_amount_of_concurrent_items = 5;
  private notifications: NotificationConfig[] = [];
  private allowNewNotifications = true;

  constructor(private readonly router: Router) {
    router.events
      .pipe(
        filter((e) => e instanceof NavigationEnd),
        map((e) => (e as NavigationEnd).url.includes(RoutingNames.permissionDenied)),
        takeUntilDestroyed(),
      )
      .subscribe((disableNotificationEvent) => {
        if (disableNotificationEvent) {
          this.allowNewNotifications = false;
          for (const notification of this.notifications) {
            this.removeNotification(notification);
          }
        } else {
          this.allowNewNotifications = true;
        }
      });
  }

  getNotifications(): NotificationConfig[] {
    return this.notifications;
  }

  private addNotification(notification: NotificationConfig) {
    if (
      this.notifications.length < NotificationService.max_amount_of_concurrent_items &&
      this.notifications[this.notifications.length - 1]?.title !== notification.title &&
      this.allowNewNotifications
    ) {
      this.notifications.push(notification);
    }
  }

  removeNotification(notification: NotificationConfig) {
    const index = this.notifications.findIndex((n) => n === notification);
    this.notifications.splice(index, 1);
  }

  addErrorNotification(title: string, message = '', dismissable = true) {
    const item = new NotificationConfigBuilder()
      .title(title)
      .description(message)
      .variant('error')
      .dismissable(dismissable)
      .duration(0) // Errors should not auto-dismiss
      .build();
    this.addNotification(item);
  }

  addInfoNotification(title: string, message = '', dismissable = true) {
    const item = new NotificationConfigBuilder()
      .title(title)
      .description(message)
      .variant('info')
      .dismissable(dismissable)
      .build();
    this.addNotification(item);
  }

  addSuccessNotification(title: string, message = '', dismissable = true) {
    const item = new NotificationConfigBuilder()
      .title(title)
      .description(message)
      .variant('success')
      .dismissable(dismissable)
      .build();
    this.addNotification(item);
  }

  addWarningNotification(title: string, message = '', dismissable = true) {
    const item = new NotificationConfigBuilder()
      .title(title)
      .description(message)
      .variant('warning')
      .dismissable(dismissable)
      .build();
    this.addNotification(item);
  }
}
